import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';

type Owed = {
  owedCents: number;
  credit: { totalCents: number; earnedCents: number; promoCents: number; people: number; top: { email: string | null; balanceCents: number; earnedCents: number }[] };
  heldCreatorPayouts: { n: number; cents: number };
  heldReaderPayouts: { n: number; cents: number };
  pendingAffiliateCommissions: { n: number; cents: number };
  stripeAvailableCents: number | null;
  stripePendingCents: number | null;
};

const money = (c: number | null | undefined) => (c == null ? '—' : `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// What we owe people out of our own Stripe balance, next to what is in it. Keep at
// least the owed total in Stripe: Hey Joe credit spent on someone's class is paid
// to that creator from our balance.
export const MoneyOwedPanel: React.FC = () => {
  const [data, setData] = useState<Owed | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/api/admin/money-owed', { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to load');
      setData(d);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const short = data && data.stripeAvailableCents != null ? data.owedCents - data.stripeAvailableCents : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Money owed</CardTitle>
          <CardDescription>Keep at least this much in Stripe. Hey Joe credit and held payouts are paid out of our balance.</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        {error && <div className="text-sm text-destructive">{error}</div>}
        {!data && loading && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
        {data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground">Owed in total</div>
                <div className="text-2xl font-bold">{money(data.owedCents)}</div>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground">In Stripe now (available)</div>
                <div className="text-2xl font-bold">{money(data.stripeAvailableCents)}</div>
                {data.stripePendingCents ? <div className="text-xs text-muted-foreground">{money(data.stripePendingCents)} pending</div> : null}
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-xs text-muted-foreground">Cushion</div>
                <div className={`text-2xl font-bold ${short != null && short > 0 ? 'text-destructive' : ''}`}>
                  {short == null ? '—' : short > 0 ? `${money(short)} short` : money(-short)}
                </div>
              </div>
            </div>
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between"><span>Hey Joe credit ({data.credit.people} people)</span><span className="font-medium">{money(data.credit.totalCents)}</span></div>
              <div className="flex justify-between pl-4 text-muted-foreground"><span>Earned (reader sessions, referrals; cashable)</span><span>{money(data.credit.earnedCents)}</span></div>
              <div className="flex justify-between pl-4 text-muted-foreground"><span>Thank-you and granted credit</span><span>{money(data.credit.promoCents)}</span></div>
              <div className="flex justify-between"><span>Held creator payouts ({data.heldCreatorPayouts.n})</span><span className="font-medium">{money(data.heldCreatorPayouts.cents)}</span></div>
              <div className="flex justify-between"><span>Held reader payouts ({data.heldReaderPayouts.n})</span><span className="font-medium">{money(data.heldReaderPayouts.cents)}</span></div>
              <div className="flex justify-between"><span>Unpaid referral commissions ({data.pendingAffiliateCommissions.n})</span><span className="font-medium">{money(data.pendingAffiliateCommissions.cents)}</span></div>
            </div>
            {data.credit.top.length > 0 && (
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">Largest credit balances</div>
                <div className="text-sm space-y-1">
                  {data.credit.top.map((t, i) => (
                    <div key={i} className="flex justify-between gap-3">
                      <span className="truncate">{t.email || 'unknown'}</span>
                      <span>{money(t.balanceCents)}{t.earnedCents > 0 ? ` (${money(t.earnedCents)} earned)` : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
